import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Avatar, Empty, Kpi, KpiGrid, Muted, PageHeader, StatusBadge } from '@/components/ui'
import { fetchColaboradores, fetchRhKpis } from './api'

export function RhPage() {
  const { canEdit } = useAuth()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('ativo')
  const [empresaFiltro, setEmpresaFiltro] = useState('visivel')
  const [page, setPage] = useState(1)

  const kpis = useQuery({
    queryKey: ['rh', 'kpis'],
    queryFn: fetchRhKpis,
  })

  const lista = useQuery({
    queryKey: ['rh', 'colaboradores', q, status, empresaFiltro, page],
    queryFn: () =>
      fetchColaboradores({
        busca: q || undefined,
        status: status || undefined,
        empresaFiltro: empresaFiltro || 'visivel',
        page,
        por_pagina: 20,
      }),
  })

  const dados = lista.data?.dados || []
  const meta = lista.data?.meta

  return (
    <section className="hi-page">
      <PageHeader
        title="Colaboradores"
        subtitle="Headcount e cadastro conectados à API. Admissão completa e GED avançado seguem no painel legado."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/colaboradores')}>
              RH legado
            </a>
            {canEdit('cargos') ? (
              <Link className="btn btn-secondary" to="/app/cargos">
                Cargos
              </Link>
            ) : null}
            {canEdit('colaboradores') ? (
              <>
                <Link className="btn btn-secondary" to="/app/rh/importar">
                  Importar
                </Link>
                <Link className="btn btn-primary" to="/app/rh/novo">
                  Novo colaborador
                </Link>
              </>
            ) : null}
          </>
        }
      />

      {kpis.isLoading ? <Muted>Carregando KPIs…</Muted> : null}
      {kpis.isError ? (
        <Alert tone="danger">
          {kpis.error instanceof ApiError ? kpis.error.message : 'Falha ao carregar KPIs.'}
        </Alert>
      ) : null}
      {kpis.data ? (
        <KpiGrid>
          <Kpi label="Ativos / total" value={kpis.data.total.toLocaleString('pt-BR')} />
          <Kpi label="Admissões no mês" value={kpis.data.adm_mes.toLocaleString('pt-BR')} />
          <Kpi label="Desligamentos" value={kpis.data.deslig_mes.toLocaleString('pt-BR')} />
          <Kpi label="Em férias" value={kpis.data.em_ferias.toLocaleString('pt-BR')} />
          <Kpi label="Afastados" value={kpis.data.afastados.toLocaleString('pt-BR')} />
          <Kpi label="Turnover %" value={kpis.data.turnover.toLocaleString('pt-BR')} />
          <Kpi label="Aval. pendentes" value={kpis.data.aval_pend.toLocaleString('pt-BR')} />
        </KpiGrid>
      ) : null}

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
          setPage(1)
          setQ(search.trim())
        }}
      >
        <input
          type="search"
          placeholder="Nome, matrícula, e-mail, CPF…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={status}
          onChange={(e) => {
            setPage(1)
            setStatus(e.target.value)
          }}
          aria-label="Status do colaborador"
        >
          <option value="">Todos os status</option>
          <option value="ativo">Ativos</option>
          <option value="ferias">Férias</option>
          <option value="afastado">Afastados</option>
          <option value="desligado">Desligados</option>
        </select>
        <select
          value={empresaFiltro}
          onChange={(e) => {
            setPage(1)
            setEmpresaFiltro(e.target.value)
          }}
          aria-label="Filtro por empresa"
        >
          <option value="visivel">Empresas visíveis</option>
          <option value="sem">Sem empresa</option>
          <option value="fora">Sem empresa / ocultas / inativas / excluídas</option>
          <option value="ocultas">Só empresas ocultas</option>
          <option value="inativas">Só empresas inativas</option>
          <option value="excluidas">Só empresas excluídas</option>
          <option value="todas">Todas</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Buscar
        </button>
      </form>

      <Muted>
        {lista.isFetching
          ? 'Atualizando…'
          : `${meta?.total ?? dados.length} colaborador(es)${
              empresaFiltro === 'visivel'
                ? ' · só empresas visíveis'
                : empresaFiltro === 'sem'
                  ? ' · sem empresa'
                  : empresaFiltro === 'fora'
                    ? ' · sem empresa / ocultas / inativas / excluídas'
                    : ''
            }`}
      </Muted>

      {lista.isError ? (
        <Alert tone="danger">
          {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
        </Alert>
      ) : null}

      <ul className="hi-list">
        {dados.map((c) => (
          <li key={c.id}>
            <Link className="card card-interactive hi-row" to={`/app/rh/${c.id}`}>
              <Avatar src={c.foto_url || c.foto} nome={c.nome} iniciais={c.iniciais} />
              <div className="hi-row-main">
                <strong>{c.nome}</strong>
                <span>
                  {c.empresa_fantasia || c.empresa_nome
                    ? `${c.empresa_fantasia || c.empresa_nome} · `
                    : ''}
                  {c.cargo_nome || 'Sem cargo'}
                  {c.departamento_nome ? ` · ${c.departamento_nome}` : ''}
                </span>
                <span>{c.email_corporativo || c.matricula || '-'}</span>
              </div>
              <StatusBadge tone={c.status && c.status !== 'ativo' ? 'inativo' : 'ativo'}>
                {c.status_display || c.status || '-'}
              </StatusBadge>
            </Link>
          </li>
        ))}
      </ul>

      {!lista.isLoading && dados.length === 0 ? <Empty>Nenhum colaborador neste filtro.</Empty> : null}

      {meta && (meta.tem_anterior || meta.tem_proxima) ? (
        <div className="hi-pager">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!meta.tem_anterior || lista.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <Muted>
            Página {meta.pagina ?? page}
            {meta.ultima_pagina ? ` de ${meta.ultima_pagina}` : ''}
          </Muted>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!meta.tem_proxima || lista.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      ) : null}
    </section>
  )
}

export default RhPage
